import { FileText } from 'lucide-react';

import { extractTemplateVars, splitTemplate } from './proactive-adapter';
import { useProactiveTemplates } from './use-proactive';

import type { MetaCategory, ProactiveTemplate } from './types';

interface Step3Props {
  category: MetaCategory;
  templateId: string | null;
  onSelectTemplate: (t: ProactiveTemplate) => void;
  vars: Record<string, string>;
  onVar: (key: string, value: string) => void;
}

function TemplatePreview({
  bodyPreview,
  vars,
}: {
  bodyPreview: string | undefined;
  vars: Record<string, string>;
}): JSX.Element | null {
  const parts = splitTemplate(bodyPreview, vars);
  if (parts.length === 0) return null;
  return (
    <div className="tpl-card__preview">
      {parts.map((p, i) =>
        p.type === 'var' ? (
          <span key={i} className="tpl-card__preview-var">
            {p.value}
          </span>
        ) : (
          <span key={i}>{p.value}</span>
        ),
      )}
    </div>
  );
}

/**
 * Passo 3 — Template & preview. Templates reais via `GET /templates?category=`.
 * D6: variáveis são índices numéricos extraídas do bodyPreview. D9: sem
 * bodyPreview, degrada honesto (só nome selecionável, sem preview/variáveis).
 * Empty state honesto: categorias sem template aprovado (service/customer_care
 * no fallback dev) mostram a mensagem real do backend (lista vazia).
 */
export function Step3Template({
  category,
  templateId,
  onSelectTemplate,
  vars,
  onVar,
}: Step3Props): JSX.Element {
  const { data, isLoading, isError } = useProactiveTemplates(category);
  const templates = data ?? [];
  const selected = templates.find((t) => t.name === templateId);
  const selectedVars = extractTemplateVars(selected?.bodyPreview);

  return (
    <>
      <div className="coleta-section">
        <span className="coleta-section__label">Templates aprovados · categoria {category}</span>
        {isLoading && (
          <div className="muted" style={{ fontSize: 12 }}>
            Carregando templates…
          </div>
        )}
        {isError && (
          <div className="muted" style={{ fontSize: 12 }}>
            Não foi possível carregar templates.
          </div>
        )}
        {!isLoading && !isError && templates.length === 0 && (
          <div className="form-row__note">
            Nenhum template aprovado para esta categoria neste tenant. Solicite ao supervisor ou
            escolha outra categoria.
          </div>
        )}
        {templates.length > 0 && (
          <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
            {templates.map((t) => (
              <div
                key={t.name}
                className={'tpl-card' + (templateId === t.name ? ' tpl-card--selected' : '')}
                onClick={() => onSelectTemplate(t)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') onSelectTemplate(t);
                }}
              >
                <div className="tpl-card__name">
                  <FileText size={11} />
                  {t.name}
                  {templateId === t.name && (
                    <span style={{ marginLeft: 'auto', color: 'var(--brand)', fontSize: 10 }}>
                      ✓ selecionado
                    </span>
                  )}
                </div>
                <TemplatePreview bodyPreview={t.bodyPreview} vars={vars} />
              </div>
            ))}
          </div>
        )}
      </div>

      {selected !== undefined && selectedVars.length > 0 && (
        <div className="coleta-section">
          <span className="coleta-section__label">Variáveis do template</span>
          <div className="muted mono" style={{ fontSize: 10, marginBottom: 4 }}>
            As variáveis destacadas no preview são substituídas no envio.
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 'var(--space-2)',
            }}
          >
            {selectedVars.map((k) => (
              <div key={k} className="coleta-field">
                <label className="coleta-field__label mono" style={{ fontSize: 10 }}>
                  {`{{${k}}}`}
                </label>
                <input
                  type="text"
                  aria-label={`Variável ${k}`}
                  value={vars[k] ?? ''}
                  onChange={(e) => onVar(k, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
